import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Moon, AlertTriangle, Brain, Activity, Sun, Pill, Dumbbell, ListChecks } from 'lucide-react'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
}

const Section = ({ icon: Icon, color, title, children }: {
  icon: typeof Moon
  color: string
  title: string
  children: React.ReactNode
}) => (
  <div style={{
    background: 'white',
    border: '1px solid rgba(26,58,92,0.06)',
    borderRadius: 16,
    padding: 14,
    marginBottom: 12,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: `${color}14`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} color={color} />
      </div>
      <h3 style={{ fontSize: 14, fontWeight: 800, color: '#1C2D41', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ fontSize: 12, color: '#3D4A5C', lineHeight: 1.55, fontFamily: F }}>
      {children}
    </div>
  </div>
)

const Item = ({ title, body, color = '#6C5CE7' }: { title: string; body: string; color?: string }) => (
  <div style={{ padding: '8px 0', borderTop: '1px solid rgba(26,58,92,0.06)', display: 'flex', gap: 8 }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, marginTop: 6, flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1C2D41' }}>{title}</div>
      <div style={{ fontSize: 11, color: '#6B7B8D', marginTop: 2, lineHeight: 1.5 }}>{body}</div>
    </div>
  </div>
)

export function SleepGuideSheet({ open, onClose }: Props) {
  const dragControls = useDragControls()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  // Рендерим через portal в document.body, чтобы фикcированное позиционирование
  // не «зашло» в transform/filter-контекст родителя (Diary имеет анимирующиеся
  // motion-карточки, которые ломали fixed-позиционирование и pointer-events).
  if (typeof document === 'undefined') return null

  const node = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 600,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="sleep-guide-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '6px 20px 6px' }}>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 40 }}>
                <div style={{
                  width: 50, height: 50, borderRadius: 14,
                  background: 'linear-gradient(135deg, #1A7A6E 0%, #24A99A 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 22px rgba(26,122,110,0.35)',
                }}>
                  <Moon size={26} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Методичка TFA
                  </div>
                  <h2 style={{ fontSize: 18, fontWeight: 900, color: '#1C2D41', margin: '2px 0 0' }}>
                    Как наладить сон
                  </h2>
                </div>
              </div>
              <p style={{ fontSize: 12, color: '#6B7B8D', margin: '10px 0 0', lineHeight: 1.5 }}>
                Без эзотерики и магния-глицината как панацеи. Только то, что реально работает и подтверждено исследованиями.
              </p>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '10px 16px 0', minHeight: 0 }}>
              {/* Сначала найди причину */}
              <Section icon={AlertTriangle} color="#E8502A" title="Сначала найди причину">
                Всё зависит от того, почему плохой сон. Одна причина — одно решение. Не угадывай.
                <Item color="#E8502A" title="Сам себе укорачиваешь сон" body="Скроллинг, сериалы, игры. Непостоянный режим в выходные. Дневной сон слишком долго или слишком поздно." />
                <Item color="#D4832A" title="Голова не выключается" body="Тревожные мысли перед сном, страх «не засну», катастрофизация: «если не высплюсь — завтра всё провалю»." />
                <Item color="#1A7A6E" title="Физиологическая причина" body="Апноэ (храп, пробуждения с удушьем), синдром беспокойных ног, боли, ГЭРБ, депрессия, тревожное расстройство." />
                <Item color="#6C5CE7" title="Среда и расписание" body="Свет, температура, шум, алкоголь, кофе поздно, физнагрузка вечером, синий свет экранов — подавляет мелатонин." />
              </Section>

              {/* КПТ-И */}
              <Section icon={Brain} color="#6C5CE7" title="КПТ-И — главный инструмент при хронической бессоннице">
                Когнитивно-поведенческая терапия инсомнии работает лучше таблеток в долгосрок. Эффект держится до 3 лет. Можно делать самостоятельно через приложения — эффективность сопоставима с очной работой.
                <Item color="#6C5CE7" title="Ограничение сна" body="Сокращаешь время в постели до реального времени сна. Создаёт давление сна — засыпаешь быстрее. Потом постепенно удлиняешь на 15 мин в неделю." />
                <Item color="#6C5CE7" title="Контроль стимулов" body="Постель = только сон и секс. Не уснул за 20 мин — встаёшь, идёшь в другую комнату, делаешь что-то спокойное. Возвращаешься только когда клонит в сон." />
                <Item color="#6C5CE7" title="Когнитивная реструктуризация" body="Разбор мыслей «если не высплюсь — провалю всё». Проверка доказательств, поиск альтернатив. Снижение гиперфокуса на сне." />
                <Item color="#6C5CE7" title="Релаксационные техники" body="Прогрессивная мышечная релаксация, дыхание 4-7-8, боди-скан. Эффективны в сочетании с другими методами, не сами по себе." />
              </Section>

              {/* Гигиена сна */}
              <Section icon={Sun} color="#D4832A" title="Гигиена сна и свет">
                Сама по себе работает слабо, но усиливает всё остальное. 72% инсомников нарушают базовую гигиену.
                <Item color="#D4832A" title="Световая терапия утром" body="Яркий свет 10 000 лк, 20–30 мин утром сдвигает циркадный ритм вперёд. Если засыпаешь поздно — свет рано утром." />
                <Item color="#D4832A" title="Блокировка синего вечером" body="За 2–3 часа до сна — приглушённый тёплый свет, очки-блокаторы или режим Night Shift / тёмная тема." />
                <Item color="#D4832A" title="Температура и кофеин" body="Спальня 18–20°C. Кофеин — отсечка за 8 часов до сна (период полураспада 5–6 часов). Алкоголь рушит REM-фазу." />
              </Section>

              {/* Мелатонин */}
              <Section icon={Pill} color="#1A7A6E" title="Мелатонин: как правильно">
                Это <b>не снотворное</b>. Сигнальный гормон: говорит мозгу «темнеет, скоро спать». Сдвигает циркадный ритм и слегка снижает время засыпания.
                <div style={{
                  marginTop: 8,
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: 'rgba(232,80,42,0.06)',
                  border: '1px solid rgba(232,80,42,0.20)',
                  fontSize: 11, color: '#8C2D14',
                }}>
                  ⚠️ Большинство OTC-доз (5–10 мг) завышены в 5–10 раз. Высокая доза даёт эффект рикошета утром и дневную вялость.
                </div>
                <Item color="#1A7A6E" title="Рабочая доза — 0.3–0.5 мг" body="За 30–60 мин до сна. Меньше = эффективнее. Buscemi et al. Cochrane Review 2006." />
                <Item color="#1A7A6E" title="Когда работает" body="Сдвиг циркадного ритма при джетлаге и delayed sleep phase. Не работает как «выключатель»." />
              </Section>

              {/* Недоспал */}
              <Section icon={Activity} color="#E8502A" title="Недоспал — что делать">
                Не панацеи, но реально работающие инструменты, когда сон был дерьмовым, а день не отменяется.
                <Item color="#E8502A" title="Креатин 20 г/сут" body="При недосыпе мозг тратит АТФ быстрее, префронтальная кора (решения/мотивация) страдает первой. Креатин восполняет фосфокреатин. McMorris 2006, 2007 — снижает падение рабочей памяти и скорости реакции при 24–36 ч недосыпа." />
                <Item color="#E8502A" title="Кофеин: малыми дозами" body="Маленькие дозы чаще лучше, чем одна большая. Большая = иллюзия бодрости → крэш → соблазн лечь в 18:00 → следующая ночь сломана. Отсечка минимум за 8 ч до сна." />
                <Item color="#9AA5B4" title="ZMA / валериана / ГАМК орально" body="Доказательная база слабая. Не тратиться." />
              </Section>

              {/* Тренироваться */}
              <Section icon={Dumbbell} color="#4A6CF7" title="Тренироваться когда недоспал?">
                Короткий ответ — <b>да</b>, в большинстве случаев. Если тренировка просто выпадет из недели — лучше недоспать и потренироваться.
                <Item color="#4A6CF7" title="Сила и мощность" body="Физическая готовность при острой депривации падает меньше, чем кажется. Oliver et al. 2009; Fullagar et al. 2015 Sports Med review." />
                <Item color="#4A6CF7" title="Что страдает" body="Субъективное ощущение усилия (RPE) и мотивация. Реальная сила — почти нет. Снижай объём, не интенсивность." />
              </Section>

              {/* Чеклист */}
              <Section icon={ListChecks} color="#34C759" title="Чеклист на каждый день">
                Не нужно делать всё сразу — выбери 3–4 актуальных и держи на этой неделе.
                <Item color="#34C759" title="Подъём в одно время ±30 мин" body="Даже в выходные. Главный синхронизатор циркадного ритма." />
                <Item color="#34C759" title="Свет в первые 30 мин после подъёма" body="Окно, прогулка или световая лампа 10 000 лк." />
                <Item color="#34C759" title="Кофеин — до 14:00" body="Период полураспада 5–6 ч, остатки в крови к вечеру." />
                <Item color="#34C759" title="Экраны — за 60 мин до сна" body="Или ночной режим + минимальная яркость." />
                <Item color="#34C759" title="Спальня — 18–20°C, темнота, тишина" body="Беруши/маска при необходимости." />
                <Item color="#34C759" title="Не лежишь в постели дольше 20 мин без сна" body="Встал → спокойная активность в другой комнате → вернулся, когда захотелось спать." />
              </Section>

              {/* Источник */}
              <div style={{
                background: 'rgba(108,92,231,0.06)',
                border: '1px solid rgba(108,92,231,0.16)',
                borderRadius: 12,
                padding: 10,
                marginBottom: 16,
                fontSize: 11,
                color: '#6B7B8D',
                lineHeight: 1.5,
              }}>
                Источники: Buscemi et al. Cochrane Review 2006, Herxheimer &amp; Petrie meta-analysis, McMorris et al. 2006/2007, Oliver et al. 2009, Fullagar et al. 2015 Sports Med review. Методичка TFA.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
  return createPortal(node, document.body)
}
